import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { ToastProvider } from './components/Toast';
import './index.css';

// ── Clipboard fallback for non-secure (http://) contexts ─────────────────────
// On a plain-HTTP LAN page, navigator.clipboard is undefined, so every
// navigator.clipboard.writeText(...) call across the app silently fails. Defining
// a single fallback here (legacy execCommand) makes ALL copy buttons work over HTTP.
if (typeof navigator !== 'undefined' && !navigator.clipboard) {
  const writeText = (text: string): Promise<void> =>
    new Promise((resolve, reject) => {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.top = '0';
        ta.style.left = '0';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        ok ? resolve() : reject(new Error('copy failed'));
      } catch (e) { reject(e as Error); }
    });
  try {
    Object.defineProperty(navigator, 'clipboard', { value: { writeText }, configurable: true });
  } catch { /* read-only in some engines; per-component fallbacks still cover it */ }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
